import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Tag } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

interface ItemGroupPickerProps {
  projectId: string;
  value: string | null;
  onChange: (v: string | null) => void;
  disabled?: boolean;
}

interface GroupListProps {
  projectId: string;
  value: string | null;
  q: string;
  setQ: (v: string) => void;
  onPick: (v: string | null) => void;
  open: boolean;
}

function GroupList({ projectId, value, q, setQ, onPick, open }: GroupListProps) {
  const { data: rows } = useQuery({
    queryKey: ['item-group-names', projectId],
    enabled: !!projectId && open,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('items')
        .select('group_name')
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .not('group_name', 'is', null);
      if (error) throw error;
      return (data ?? []) as { group_name: string | null }[];
    },
  });

  const groups = useMemo(() => {
    return [...new Set((rows ?? []).map((r) => r.group_name))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'vi')) as string[];
  }, [rows]);

  const trimmedQ = q.trim();
  const normalizedQ = trimmedQ.toLowerCase();
  const exists = useMemo(() => {
    if (!trimmedQ) return true;
    return groups.some((g) => g.toLowerCase() === normalizedQ);
  }, [groups, trimmedQ, normalizedQ]);

  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder="Tìm hoặc tạo nhóm..."
        value={q}
        onValueChange={setQ}
      />
      <CommandList>
        {groups.filter(g => !normalizedQ || g.toLowerCase().includes(normalizedQ)).length === 0 && !trimmedQ && (
          <CommandEmpty>Gõ tên để tạo nhóm mới.</CommandEmpty>
        )}
        <CommandGroup>
          {value != null && (
            <CommandItem onSelect={() => onPick(null)}>
              <span className="text-muted-foreground">Bỏ nhóm</span>
            </CommandItem>
          )}
          {groups.filter(g => !normalizedQ || g.toLowerCase().includes(normalizedQ)).map((g) => {
            const active = value === g;
            return (
              <CommandItem key={g} onSelect={() => onPick(g.trim())}>
                <span className="flex-1 truncate">{g}</span>
                {active && <Check className="ml-2 h-4 w-4" />}
              </CommandItem>
            );
          })}
          {trimmedQ && !exists && (
            <CommandItem onSelect={() => onPick(trimmedQ)}>
              <span className="flex-1 truncate">
                ＋ Tạo «{trimmedQ}»
              </span>
            </CommandItem>
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

export default function ItemGroupPicker({
  projectId,
  value,
  onChange,
  disabled,
}: ItemGroupPickerProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const handleSelect = useCallback(
    (v: string | null) => {
      onChange(v);
      setOpen(false);
      setQ('');
    },
    [onChange]
  );

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={value ?? 'Chưa phân nhóm'}
          className={cn(
            'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-muted transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-50',
            value ? 'text-primary' : 'text-muted-foreground/50'
          )}
        >
          <Tag className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        {open && (
          <GroupList
            projectId={projectId}
            value={value}
            q={q}
            setQ={setQ}
            onPick={handleSelect}
            open={open}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
